import React from 'react';
import {
  HelpCircle,
  Flower,
  ShieldCheck,
  Wallet
} from 'lucide-react';
import FAQSection from '../Components/FAQSection';
import FreeConsultationCTA from '../Components/FreeConsultationCTA';
import SEO from '../Components/shared/SEO';


const SpaFAQPage = () => {
  const massageServiceFAQs = [
    {
      question: "What types of massage services do you offer?",
      answer: "Clients can book Swedish, Deep Tissue, Hot Stone, Sports, Prenatal, and Reflexology massage. Each session is matched to a verified therapist experienced in your chosen treatment."
    },
    {
      question: "How long are your massage sessions?",
      answer: "Most sessions run 60 or 90 minutes depending on the treatment. You'll see the exact duration and price for each massage type before you book."
    },
    {
      question: "Where can I book a massage?",
      answer: "You can choose to have your therapist come to your home or office, or book a room at one of our partner hotels, guesthouses, or spas directly through the booking flow."
    },
    {
      question: "Do I need to book in advance?",
      answer: "We recommend booking ahead to guarantee availability, especially in peak zones and at popular times. Our real-time matching system will try to pair you with an available verified therapist as quickly as possible."
    },
    {
      question: "Are your massage therapists verified?",
      answer: "Yes. Every therapist on Serenoly goes through an admin-reviewed verification process, including ID and certification checks, before they can accept bookings on the platform."
    }
  ];

  const platformFAQs = [
    {
      question: "How does escrow-protected payment work?",
      answer: "When you book, your payment is held securely in escrow via Paystack. It's only released to your therapist once both you and the therapist confirm the session is complete, so you're never charged for a session that didn't happen."
    },
    {
      question: "What is the Serenoly wallet?",
      answer: "Therapists and venue partners earn into a two-bucket platform wallet: withdrawable earnings from completed sessions, and non-withdrawable top-up credit. Withdrawals are verified against a linked bank account and processed via Paystack Transfers or manual admin payout, Monday through Friday."
    },
    {
      question: "Can I cancel or reschedule a booking?",
      answer: "Yes, bookings can be cancelled from your order dashboard before a therapist begins the session. If you run into an issue with a booking, our admin team also handles dispute resolution."
    },
    {
      question: "How do I become a therapist on Serenoly?",
      answer: "Sign up and select the therapist role, then submit your ID and certification documents for admin review. Once verified, you can set your coverage zone and massage-type preferences and start receiving real-time order offers."
    },
    {
      question: "How do venues or hotels become partners?",
      answer: "Hotels, guesthouses, and spas can sign up as venue partners, list rooms and availability after admin verification, and earn a share of bookings made against their rooms."
    }
  ];

  const trustSafetyFAQs = [
    {
      question: "How does Serenoly verify therapists and venues?",
      answer: "All therapist and venue accounts are reviewed by our admin team, including identity and certification documents, before they can appear as bookable on the platform."
    },
    {
      question: "What happens if I have an issue after a session?",
      answer: "You can rate and review every completed session. Ratings feed our quality-score-driven matching, and any serious issues can be escalated through our dispute resolution process."
    }
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [...massageServiceFAQs, ...platformFAQs, ...trustSafetyFAQs].map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-accent-lighter font-body py-12 px-4 sm:px-6 lg:px-8">
      <SEO
        title="Frequently Asked Questions"
        path="/faq"
        description="Answers to common questions about booking a massage therapist online, escrow-protected payments, wallets, and becoming a verified therapist or venue partner on Serenoly."
        jsonLd={jsonLd}
      />
      <div className="max-w-4xl mx-auto pt-20">
        <div className="text-center mb-12">
          <HelpCircle className="mx-auto w-12 h-12 text-primary mb-4" />
          <h1 className="text-3xl md:text-4xl font-extrabold font-heading text-dim">Frequently Asked Questions</h1>
          <p className="mt-4 text-lg md:text-xl text-gray-600">
            Find answers to the most common questions about booking, payments, and our therapist marketplace
          </p>
        </div>

        <FAQSection
          title="Massage Services"
          icon={Flower}
          faqs={massageServiceFAQs}
        />

        <FAQSection
          title="Booking, Payments & Wallets"
          icon={Wallet}
          faqs={platformFAQs}
        />

        <FAQSection
          title="Trust & Safety"
          icon={ShieldCheck}
          faqs={trustSafetyFAQs}
        />

        <FreeConsultationCTA />
      </div>
    </div>
  );
};

export default SpaFAQPage;